// built-from: /engagement-brief-surface v1 impl:engagement-brief-surface role:implementation
// built-from: /objectives-surface v1 impl:objectives-surface role:implementation
// built-from: /domains-stakeholders-surface v1 impl:domains-stakeholders-surface role:implementation
// Feature 075: the three brief-first capture surfaces. Every field the
// facilitator fills is directly editable in place — the consultant's edit
// wins and lands through the SAME capabilities the facilitator uses. No
// propose/accept ceremony.
//
// Presentational: documents in, capability edits out (OutcomeSurfaceProps'
// onEmitEdit). The workspaces own sessions, fetching and refresh.

import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { OutcomeSurfaceProps } from '../facilitation/surfaceBinding';
import type { EngagementActivity } from '../hooks/useEngagementSetup';
import '../styles/BriefFirstWorkspace.css';

export interface BriefDocument {
  version: number;
  client: string;
  narrative: string;
  goal: string;
  engagementType: string;
}

export interface ObjectiveEntry {
  id: string;
  what: string;
  /** Older seeded documents named the objective statement `text`. */
  text?: string;
  why: string;
  who: string;
  successMeasure: string;
  source?: string;
}

export interface ObjectivesDocument {
  version: number;
  diagnosis: string;
  guidingPolicy: string;
  objectives: ObjectiveEntry[];
}

export interface StakeholderEntry {
  id: string;
  name: string;
  role: string;
  source?: string;
  // 095: a person may be attached to a process rather than only a domain.
  processId?: string | null;
}

export interface DomainEntry {
  id: string;
  name: string;
  description: string;
  source?: string;
  stakeholders: StakeholderEntry[];
}

export interface DomainsDocument {
  version: number;
  domains: DomainEntry[];
  unassignedStakeholders: StakeholderEntry[];
}

// ── Inline editing primitives ──────────────────────────────────────

function EditableText({ label, value, placeholder, multiline, source, onCommit }: {
  label: string;
  value: string;
  placeholder: string;
  multiline?: boolean;
  source?: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  const commit = () => {
    setEditing(false);
    if (draft.trim() !== value.trim()) onCommit(draft.trim());
  };
  const shared = {
    value: draft,
    placeholder,
    onFocus: () => setEditing(true),
    onBlur: commit,
    'aria-label': label,
  };
  return (
    <label className="outline-field">
      <span className="outline-field-label">
        {label}
        {source === 'brief' ? <em className="outline-from-brief">from Brief</em> : null}
      </span>
      {multiline ? (
        <textarea
          rows={4}
          {...shared}
          onChange={(event) => setDraft(event.target.value)}
        />
      ) : (
        <input
          {...shared}
          onChange={(event) => setDraft(event.target.value)}
        />
      )}
    </label>
  );
}

function AddInline({ label, placeholder, onAdd }: {
  label: string;
  placeholder: string;
  onAdd: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const commit = () => {
    const value = draft.trim();
    setOpen(false);
    setDraft('');
    if (value) onAdd(value);
  };
  if (!open) {
    return (
      <button type="button" className="outline-add" onClick={() => setOpen(true)}>
        <Plus size={14} aria-hidden="true" /> {label}
      </button>
    );
  }
  return (
    <input
      className="outline-add-input"
      autoFocus
      value={draft}
      placeholder={placeholder}
      aria-label={label}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') { event.preventDefault(); commit(); }
        if (event.key === 'Escape') { setOpen(false); setDraft(''); }
      }}
    />
  );
}

// datetime-local wants local `YYYY-MM-DDTHH:mm`; the API stores ISO strings.
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

// ── Brief surface ──────────────────────────────────────────────────

export interface BriefPlanHandlers {
  onRemove: (activityKey: string) => void;
  onAdd: (activityKey: string) => void;
  onActivityField: (activityId: string, field: string, value: unknown) => void;
  onOpen: (activity: EngagementActivity) => void;
}

// 075's hardcoded pair, kept only as the fallback while the activity pool
// projection loads (077: the live add-list comes from the pool endpoint).
export const BRIEF_ACTIVITY_OPTIONS: ReadonlyArray<{ key: string; label: string }> = Object.freeze([
  { key: 'objectives', label: 'Objectives' },
  { key: 'domains-stakeholders', label: 'Business map' },
]);

function activityKeyOf(activity: EngagementActivity): string {
  return activity.specialistMethod || activity.id;
}

// 077: the passive prompts. Empty and thin slots steer the story without a
// spoken question — one quiet line under the brief fields.
function thinSlots(brief: BriefDocument): string[] {
  const slots: string[] = [];
  if (!(brief.narrative || '').trim()) slots.push('the story so far');
  if (!(brief.goal || '').trim()) slots.push('goal');
  if (!(brief.engagementType || '').trim()) slots.push('engagement type');
  return slots;
}

export function BriefOutcomeSurface(props: OutcomeSurfaceProps & {
  brief?: BriefDocument;
  activities?: EngagementActivity[];
  plan?: BriefPlanHandlers;
  activityOptions?: ReadonlyArray<{ key: string; label: string }>;
  activityHints?: Record<string, string>;
}) {
  const {
    onEmitEdit,
    brief,
    activities = [],
    plan,
    activityOptions = BRIEF_ACTIVITY_OPTIONS,
    activityHints = {},
  } = props;
  if (!brief) return <div className="outline-surface" role="status">Loading the brief...</div>;
  const ordered = [...activities].sort((a, b) => a.order - b.order);
  const presentKeys = new Set(ordered.map(activityKeyOf));
  const addable = activityOptions.filter((option) => !presentKeys.has(option.key));
  const gaps = thinSlots(brief);
  return (
    <div className="outline-surface" aria-label="The brief">
      <EditableText
        label="Narrative"
        value={brief.narrative}
        placeholder="The background story of this engagement, in your words. Talk to the facilitator or type here."
        multiline
        onCommit={(text) => onEmitEdit({ capabilityId: 'engagement-brief.set_narrative', input: { text } })}
      />
      <EditableText
        label="Goal"
        value={brief.goal}
        placeholder="The goal the client articulated."
        onCommit={(text) => onEmitEdit({ capabilityId: 'engagement-brief.set_goal', input: { text } })}
      />
      <EditableText
        label="Engagement type"
        value={brief.engagementType}
        placeholder="e.g. phased modernisation programme, timeboxed POC"
        onCommit={(text) => onEmitEdit({ capabilityId: 'engagement-brief.set_engagement_type', input: { text } })}
      />
      {gaps.length > 0 ? (
        <p className="outline-gaps" role="note">Still thin: {gaps.join(', ')}</p>
      ) : null}

      <section className="outline-plan" aria-label="The plan">
        <h2 className="outline-plan-title">
          Engagement activities <span>planned work</span>
        </h2>
        {ordered.length === 0 ? (
          <p className="outline-plan-empty">
            No activities yet. Choose an activity when you are ready to add it
            to this engagement.
          </p>
        ) : null}
        <ol className="outline-plan-sequence">
          {ordered.map((activity) => (
            <li key={activity.id} className="outline-plan-card">
              <div className="outline-plan-card-head">
                <button
                  type="button"
                  className="outline-plan-card-name"
                  onClick={() => plan?.onOpen(activity)}
                >
                  {activity.name}
                </button>
                <button
                  type="button"
                  className="outline-plan-remove"
                  aria-label={`Remove ${activity.name}`}
                  onClick={() => plan?.onRemove(activityKeyOf(activity))}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
              {activity.purpose ? (
                <p className="outline-plan-why">why: {activity.purpose}</p>
              ) : null}
              {activityHints[activityKeyOf(activity)] ? (
                <p className="outline-gaps outline-gaps--card" role="note">
                  {activityHints[activityKeyOf(activity)]}
                </p>
              ) : null}
              <div className="outline-plan-meta">
                <label>
                  <span>owner</span>
                  <input
                    defaultValue={activity.owner}
                    aria-label={`${activity.name} owner`}
                    onBlur={(event) => {
                      const next = event.target.value.trim();
                      if (next && next !== activity.owner) {
                        plan?.onActivityField(activity.id, 'owner', next);
                      }
                    }}
                  />
                </label>
                <label>
                  <span>starts</span>
                  <input
                    type="datetime-local"
                    defaultValue={toLocalInput(activity.scheduledStart)}
                    aria-label={`${activity.name} scheduled start`}
                    onChange={(event) => {
                      plan?.onActivityField(activity.id, 'scheduledStart', fromLocalInput(event.target.value));
                    }}
                  />
                </label>
              </div>
            </li>
          ))}
        </ol>
        {addable.length > 0 ? (
          <div className="outline-plan-add">
            {addable.map((option) => (
              <button
                key={option.key}
                type="button"
                className="outline-add"
                onClick={() => plan?.onAdd(option.key)}
              >
                <Plus size={14} aria-hidden="true" /> {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

// ── Generic activity-notes surface (082) ───────────────────────────
// One screen for every notes-shaped catalogue activity: the captured notes,
// inline-editable, addable, removable — beside the same chat. The activity
// key parameterises the capability ids; nothing here names an activity.

export interface ActivityNoteEntry {
  id: string;
  title: string;
  detail: string;
  source?: string;
}

export interface ActivityNotesDocument {
  version: number;
  notes: ActivityNoteEntry[];
}

export function ActivityNotesSurface(props: OutcomeSurfaceProps & {
  activityKey?: string;
  notesDoc?: ActivityNotesDocument;
}) {
  const { onEmitEdit, activityKey = '', notesDoc } = props;
  if (!notesDoc) return <div className="outline-surface" role="status">Loading...</div>;
  const captureId = `activity-${activityKey}.capture_note`;
  const removeId = `activity-${activityKey}.remove_note`;
  return (
    <div className="outline-surface" aria-label="Captured notes">
      {notesDoc.notes.length === 0 ? (
        <p className="outline-plan-empty">
          Nothing captured here yet. Talk it through with the facilitator, or
          add a note yourself.
        </p>
      ) : null}
      <ul className="outline-notes">
        {notesDoc.notes.map((note) => (
          <li key={note.id} className="outline-plan-card">
            <div className="outline-plan-card-head">
              <strong className="outline-note-title">{note.title || note.id}</strong>
              <button
                type="button"
                className="outline-plan-remove"
                aria-label={`Remove ${note.title || note.id}`}
                onClick={() => onEmitEdit({ capabilityId: removeId, input: { id: note.id } })}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
            <EditableText
              label={`${note.title || note.id} detail`}
              value={note.detail}
              placeholder="The detail, as it was said."
              multiline
              source={note.source}
              onCommit={(detail) => onEmitEdit({ capabilityId: captureId, input: { id: note.id, detail } })}
            />
          </li>
        ))}
      </ul>
      <AddInline
        label="Add a note"
        placeholder="A short title for what was learned"
        onAdd={(title) => onEmitEdit({ capabilityId: captureId, input: { title } })}
      />
    </div>
  );
}

// ── Objectives surface ─────────────────────────────────────────────

export function ObjectivesOutcomeSurface(props: OutcomeSurfaceProps & {
  objectivesDoc?: ObjectivesDocument;
}) {
  const { onEmitEdit, objectivesDoc } = props;
  const [draftOpen, setDraftOpen] = useState(false);
  if (!objectivesDoc) return <div className="outline-surface" role="status">Loading objectives...</div>;
  const refine = (id: string, field: keyof ObjectiveEntry, value: string) => onEmitEdit({
    capabilityId: 'engagement-objectives.capture_objective',
    input: { id, [field]: value },
  });
  return (
    <div className="outline-surface" aria-label="Objectives">
      <EditableText
        label="Diagnosis"
        value={objectivesDoc.diagnosis}
        placeholder="The real challenge underneath what the client asked for."
        multiline
        onCommit={(text) => onEmitEdit({ capabilityId: 'engagement-objectives.set_diagnosis', input: { text } })}
      />
      <EditableText
        label="Guiding policy"
        value={objectivesDoc.guidingPolicy}
        placeholder="The overall approach that addresses the diagnosis."
        onCommit={(text) => onEmitEdit({ capabilityId: 'engagement-objectives.set_guiding_policy', input: { text } })}
      />

      <section aria-label="Objective list">
        <h2 className="outline-plan-title">Objectives</h2>
        {objectivesDoc.objectives.length === 0 && !draftOpen ? (
          <p className="outline-plan-empty">No objectives captured yet.</p>
        ) : null}
        <ol className="outline-cards">
          {objectivesDoc.objectives.map((objective, index) => {
            const what = objective.what || objective.text || '';
            return (
            <li key={objective.id} className="outline-card">
              <div className="outline-card-head">
                <strong>{index + 1}.</strong>
                {objective.source === 'brief' ? <em className="outline-from-brief">from Brief</em> : null}
                <button
                  type="button"
                  className="outline-plan-remove"
                  aria-label={`Remove objective ${what || objective.id}`}
                  onClick={() => onEmitEdit({
                    capabilityId: 'engagement-objectives.remove_objective',
                    input: { id: objective.id },
                  })}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
              <EditableText label="What" value={what} placeholder="What changes?" onCommit={(v) => refine(objective.id, 'what', v)} />
              <EditableText label="Why" value={objective.why} placeholder="Why does it matter?" onCommit={(v) => refine(objective.id, 'why', v)} />
              <EditableText label="Who" value={objective.who} placeholder="Who owns it?" onCommit={(v) => refine(objective.id, 'who', v)} />
              <EditableText label="Success measure" value={objective.successMeasure} placeholder="— not yet set —" onCommit={(v) => refine(objective.id, 'successMeasure', v)} />
            </li>
          );})}
        </ol>
        {draftOpen ? (
          <input
            className="outline-add-input"
            autoFocus
            placeholder="What should this objective achieve?"
            aria-label="New objective"
            onBlur={(event) => {
              const what = event.target.value.trim();
              setDraftOpen(false);
              if (what) onEmitEdit({ capabilityId: 'engagement-objectives.capture_objective', input: { what } });
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
              if (event.key === 'Escape') setDraftOpen(false);
            }}
          />
        ) : (
          <button type="button" className="outline-add" onClick={() => setDraftOpen(true)}>
            <Plus size={14} aria-hidden="true" /> objective
          </button>
        )}
      </section>
    </div>
  );
}

// ── Business map surface (095) ──────────────────────────────────────
// Replaces the domains-and-sub-domains taxonomy: domains organise
// PROCESSES (from the shared register, not a hand-kept list), each with an
// importance, a why and a storm rank; people sit under a domain or a
// process. A process the consultant has not placed sits under "Not placed
// yet" — which includes anything Event Storming narrated on its own.

export interface BusinessMapProcessEntry {
  processId: string;
  name: string;
  order: number;
  state: 'empty' | 'narrated' | 'walked' | 'agreed';
  source?: 'brief' | 'narration' | 'consultant' | 'domains';
  stepCount: number;
  updatedAt?: string;
  removedAt: string | null;
  domainId: string | null;
  importance: 'core' | 'supporting' | 'generic' | null;
  rank: number | null;
  why: string;
}

export interface BusinessMapRegister {
  processes: BusinessMapProcessEntry[];
}

const IMPORTANCE_OPTIONS: ReadonlyArray<'core' | 'supporting' | 'generic'> = Object.freeze(['core', 'supporting', 'generic']);

// A person's row: the name is a plain label (renamed through the
// conversation, not this field); role stays directly editable, as does
// removing them. `attachment` decides which capture the edit carries —
// a domain, a process, or neither (unassigned).
function StakeholderRow({ stakeholder, attachment, onEmitEdit }: {
  stakeholder: StakeholderEntry;
  attachment: { domain: string } | { process: string } | null;
  onEmitEdit: OutcomeSurfaceProps['onEmitEdit'];
}) {
  const refine = (role: string) => onEmitEdit({
    capabilityId: 'engagement-domains.capture_stakeholder',
    input: {
      id: stakeholder.id,
      name: stakeholder.name,
      role,
      ...(attachment ?? {}),
    },
  });
  return (
    <li className="outline-stakeholder">
      <strong className="outline-stakeholder-name">{stakeholder.name}</strong>
      <input
        defaultValue={stakeholder.role}
        placeholder="role"
        aria-label={`${stakeholder.name} role`}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next !== stakeholder.role) refine(next);
        }}
      />
      <button
        type="button"
        className="outline-plan-remove"
        aria-label={`Remove ${stakeholder.name}`}
        onClick={() => onEmitEdit({
          capabilityId: 'engagement-domains.remove_stakeholder',
          input: { id: stakeholder.id },
        })}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </li>
  );
}

// A process's people render as compact chips inside its row — name and a
// remove control. Role stays editable at the domain/unassigned level; a
// chip here is enough to see who is on the hook for this process.
function ProcessPersonChip({ stakeholder, onEmitEdit }: {
  stakeholder: StakeholderEntry;
  onEmitEdit: OutcomeSurfaceProps['onEmitEdit'];
}) {
  return (
    <li className="outline-chip">
      <span>{stakeholder.name}</span>
      <button
        type="button"
        aria-label={`Remove ${stakeholder.name}`}
        onClick={() => onEmitEdit({
          capabilityId: 'engagement-domains.remove_stakeholder',
          input: { id: stakeholder.id },
        })}
      >
        <X size={12} aria-hidden="true" />
      </button>
    </li>
  );
}

// The why is a single inline field: it shows the value it holds, never a
// plain-text restatement beside it. Commits on blur or Enter, like every
// other inline field on this surface.
function BusinessMapWhyField({ process, onEmitEdit }: {
  process: BusinessMapProcessEntry;
  onEmitEdit: OutcomeSurfaceProps['onEmitEdit'];
}) {
  const [draft, setDraft] = useState(process.why || '');
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(process.why || '');
  }, [process.why, editing]);
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (process.why || '').trim()) {
      onEmitEdit({
        capabilityId: 'engagement-domains.capture_process',
        input: { id: process.processId, why: next },
      });
    }
  };
  return (
    <input
      className="bm-why"
      aria-label={`Why ${process.name} matters`}
      placeholder="Why it matters"
      value={draft}
      onFocus={() => setEditing(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
    />
  );
}

// One compact row per process, to the quality of the Event Storming
// switcher: the name opens it (accessible name "Open <name>"), a rank
// badge and "from Brief" sit beside it, importance reads as three short
// choices in one segmented control (the full sentence lives in each
// button's accessible label), the why is one inline field on its own
// line, and people show as chips with the existing add-inline.
function BusinessMapProcessRow({ process, domains, showPlaceSelect, onEmitEdit, onOpenProcess, stakeholders }: {
  process: BusinessMapProcessEntry;
  domains: DomainEntry[];
  showPlaceSelect: boolean;
  onEmitEdit: OutcomeSurfaceProps['onEmitEdit'];
  onOpenProcess: (processId: string) => void;
  stakeholders: StakeholderEntry[];
}) {
  return (
    <li className="bm-row">
      <div className="bm-row-head">
        <button
          type="button"
          className="bm-row-name"
          aria-label={`Open ${process.name}`}
          onClick={() => onOpenProcess(process.processId)}
        >
          {process.name}
        </button>
        {process.rank != null ? <span className="bm-rank">{`#${process.rank}`}</span> : null}
        {process.source === 'brief' ? <em className="outline-from-brief">from Brief</em> : null}
        <div className="bm-importance" role="group" aria-label={`${process.name} importance`}>
          {IMPORTANCE_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={`bm-importance-btn bm-importance-btn--${option}${process.importance === option ? ' is-active' : ''}`}
              aria-pressed={process.importance === option}
              aria-label={`${process.name}: mark ${option}`}
              onClick={() => onEmitEdit({
                capabilityId: 'engagement-domains.capture_process',
                input: { id: process.processId, importance: option },
              })}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <BusinessMapWhyField process={process} onEmitEdit={onEmitEdit} />
      {showPlaceSelect ? (
        <label className="bm-row-place">
          <span className="outline-field-label">{`Place ${process.name} in`}</span>
          <select
            aria-label={`Place ${process.name} in`}
            defaultValue=""
            onChange={(event) => {
              const domain = event.target.value;
              if (domain) onEmitEdit({
                capabilityId: 'engagement-domains.capture_process',
                input: { id: process.processId, domain },
              });
            }}
          >
            <option value="" disabled>Choose a domain</option>
            {domains.map((domain) => (
              <option key={domain.id} value={domain.id}>{domain.name}</option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="bm-row-people">
        <ul className="outline-chiplist">
          {stakeholders.map((stakeholder) => (
            <ProcessPersonChip key={stakeholder.id} stakeholder={stakeholder} onEmitEdit={onEmitEdit} />
          ))}
        </ul>
        <AddInline
          label="person"
          placeholder="Name"
          onAdd={(name) => onEmitEdit({
            capabilityId: 'engagement-domains.capture_stakeholder',
            input: { name, process: process.processId },
          })}
        />
      </div>
    </li>
  );
}

// The storm order rail: ranked processes in rank order, reordered by hand.
// A move emits the FULL ranked id order after the swap — the seam
// renumbers dense from 1.
function BusinessMapStormOrder({ ranked, onEmitEdit }: {
  ranked: BusinessMapProcessEntry[];
  onEmitEdit: OutcomeSurfaceProps['onEmitEdit'];
}) {
  const move = (processId: string, direction: 'up' | 'down') => {
    const ids = ranked.map((process) => process.processId);
    const index = ids.indexOf(processId);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || swapIndex < 0 || swapIndex >= ids.length) return;
    [ids[index], ids[swapIndex]] = [ids[swapIndex], ids[index]];
    onEmitEdit({ capabilityId: 'engagement-domains.rank_processes', input: { ids } });
  };
  return (
    <section aria-label="Storm order" className="outline-card bm-storm">
      <h2 className="outline-plan-title">Storm order</h2>
      {ranked.length === 0 ? (
        <p className="outline-plan-empty">
          Rank the processes that matter most — "if we could only storm
          three, which three?" — and the order builds here.
        </p>
      ) : (
        <ol className="bm-storm-list">
          {ranked.map((process, index) => (
            <li key={process.processId} className="bm-storm-item">
              <span className="bm-storm-text">{`#${process.rank} ${process.name}`}</span>
              <span className="bm-storm-actions">
                <button
                  type="button"
                  className="bm-storm-btn"
                  aria-label={`Move ${process.name} up`}
                  disabled={index === 0}
                  onClick={() => move(process.processId, 'up')}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="bm-storm-btn"
                  aria-label={`Move ${process.name} down`}
                  disabled={index === ranked.length - 1}
                  onClick={() => move(process.processId, 'down')}
                >
                  ↓
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function BusinessMapSurface(props: OutcomeSurfaceProps & {
  domainsDoc?: DomainsDocument;
  register?: BusinessMapRegister;
  onOpenProcess?: (processId: string) => void;
}) {
  const { onEmitEdit, domainsDoc, register, onOpenProcess = () => {} } = props;
  if (!domainsDoc || !register) return <div className="outline-surface" role="status">Loading the business map...</div>;

  const liveProcesses = register.processes.filter((process) => !process.removedAt);
  const processesByDomain = new Map<string, BusinessMapProcessEntry[]>();
  const unplacedProcesses: BusinessMapProcessEntry[] = [];
  for (const process of liveProcesses) {
    if (process.domainId) {
      const list = processesByDomain.get(process.domainId) ?? [];
      list.push(process);
      processesByDomain.set(process.domainId, list);
    } else {
      unplacedProcesses.push(process);
    }
  }
  const ranked = liveProcesses
    .filter((process): process is BusinessMapProcessEntry & { rank: number } => process.rank != null)
    .sort((a, b) => a.rank - b.rank);
  const unplacedProcessIds = new Set(unplacedProcesses.map((process) => process.processId));

  return (
    <div className="outline-surface" aria-label="Business map">
      {domainsDoc.domains.map((domain) => {
        const processes = processesByDomain.get(domain.id) ?? [];
        const processIds = new Set(processes.map((process) => process.processId));
        const domainLevelStakeholders = domain.stakeholders.filter(
          (stakeholder) => !stakeholder.processId || !processIds.has(stakeholder.processId),
        );
        return (
          <section key={domain.id} aria-label={domain.name} className="outline-card bm-domain">
            <div className="outline-card-head bm-domain-head">
              {domain.source === 'brief' ? <em className="outline-from-brief">from Brief</em> : null}
              <button
                type="button"
                className="outline-plan-remove"
                aria-label={`Remove domain ${domain.name}`}
                onClick={() => onEmitEdit({
                  capabilityId: 'engagement-domains.remove_domain',
                  input: { id: domain.id },
                })}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
            <EditableText
              label="Domain name"
              value={domain.name}
              placeholder="Domain name"
              onCommit={(name) => onEmitEdit({
                capabilityId: 'engagement-domains.capture_domain',
                input: { id: domain.id, name, description: domain.description },
              })}
            />
            <EditableText
              label="Description"
              value={domain.description}
              placeholder="What this domain covers."
              multiline
              onCommit={(description) => onEmitEdit({
                capabilityId: 'engagement-domains.capture_domain',
                input: { id: domain.id, name: domain.name, description },
              })}
            />
            {processes.length === 0 ? <p className="outline-plan-empty">No processes placed here yet.</p> : (
              <ul className="bm-rows">
                {processes.map((process) => (
                  <BusinessMapProcessRow
                    key={process.processId}
                    process={process}
                    domains={domainsDoc.domains}
                    showPlaceSelect={false}
                    onEmitEdit={onEmitEdit}
                    onOpenProcess={onOpenProcess}
                    stakeholders={domain.stakeholders.filter((stakeholder) => stakeholder.processId === process.processId)}
                  />
                ))}
              </ul>
            )}
            <div className="outline-card-sub">
              <span>People</span>
              <ul className="outline-stakeholders">
                {domainLevelStakeholders.map((stakeholder) => (
                  <StakeholderRow
                    key={stakeholder.id}
                    stakeholder={stakeholder}
                    attachment={{ domain: domain.id }}
                    onEmitEdit={onEmitEdit}
                  />
                ))}
              </ul>
              <AddInline
                label="person"
                placeholder="Name"
                onAdd={(name) => onEmitEdit({
                  capabilityId: 'engagement-domains.capture_stakeholder',
                  input: { name, domain: domain.id },
                })}
              />
            </div>
          </section>
        );
      })}
      <AddInline
        label="domain"
        placeholder="Domain name"
        onAdd={(name) => onEmitEdit({
          capabilityId: 'engagement-domains.capture_domain',
          input: { name },
        })}
      />

      <section aria-label="Not placed yet" className="outline-card bm-unplaced">
        <h2 className="outline-plan-title">Not placed yet</h2>
        {unplacedProcesses.length === 0 ? <p className="outline-plan-empty">Every named process has a home.</p> : (
          <ul className="bm-rows">
            {unplacedProcesses.map((process) => (
              <BusinessMapProcessRow
                key={process.processId}
                process={process}
                domains={domainsDoc.domains}
                showPlaceSelect
                onEmitEdit={onEmitEdit}
                onOpenProcess={onOpenProcess}
                stakeholders={domainsDoc.unassignedStakeholders.filter((stakeholder) => stakeholder.processId === process.processId)}
              />
            ))}
          </ul>
        )}
        <div className="outline-card-sub">
          <span>People</span>
          <ul className="outline-stakeholders">
            {domainsDoc.unassignedStakeholders
              .filter((stakeholder) => !stakeholder.processId || !unplacedProcessIds.has(stakeholder.processId))
              .map((stakeholder) => (
                <StakeholderRow
                  key={stakeholder.id}
                  stakeholder={stakeholder}
                  attachment={null}
                  onEmitEdit={onEmitEdit}
                />
              ))}
          </ul>
          <AddInline
            label="person"
            placeholder="Name"
            onAdd={(name) => onEmitEdit({
              capabilityId: 'engagement-domains.capture_stakeholder',
              input: { name },
            })}
          />
        </div>
      </section>

      <BusinessMapStormOrder ranked={ranked} onEmitEdit={onEmitEdit} />
    </div>
  );
}
